import type { NunjucksEnvironment, NunjucksLoader } from '../types/nunjucks-browser.ts';
import { toCamelCase, formatDate, formatNumero } from './utils.ts';
import { getCasseMap } from './casse-info.ts';
import { getSindacatiMap, getSindacatoLabel } from './sindacati-info.ts';

// --- Costanti (allineate a web/boot/template-engine.ts) ---

const MESI = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'] as const;

const STATO_BADGE: Record<string, string> = {
  // Stati deleghe
  CONFERMATA: 'success',
  DA_PRESENTARE: 'warning',
  PRESENTATA: 'info',
  RIFIUTATA: 'danger',
  // Stati lavoratore (V=vigente, T=trasferito, C=cessato, D=deceduto)
  V: 'success',
  T: 'warning',
  C: 'danger',
  D: 'dark',
};
const STATO_LAVORATORE: Record<string, string> = {
  V: 'Attivo',
  T: 'Trasferito',
  C: 'Cessato',
  D: 'Deceduto',
};
const ICONA_STATO_LAVORATORE: Record<string, string> = {
  V: 'bi-person-check',
  T: 'bi-arrow-left-right',
  C: 'bi-person-x',
  D: 'bi-person-dash',
};
const STATO_DELEGA_BADGE: Record<string, string> = {
  BOZZA: 'secondary',
  DA_PRESENTARE: 'info',
  PRESENTATA: 'primary',
  CONFERMATA: 'success',
  ARCHIVIATA: 'danger',
};

// --- Template store ---

let templateResources: Record<string, string> = {};

export function setTemplates(views: Record<string, string>): void {
  templateResources = views;
}

// --- Custom loader ---

const loader: NunjucksLoader = {
  getSource(name: string) {
    const key = toCamelCase(name);
    const fromStore = templateResources[key];
    if (fromStore !== undefined) {
      return { src: fromStore, path: name, noCache: true };
    }

    const el = document.getElementById(name);
    if (el && (el.getAttribute('type') === 'text/html' || el.getAttribute('type') === 'text/nunjucks')) {
      return { src: el.innerHTML, path: name, noCache: true };
    }

    throw new Error(`Template "${name}" non trovato (key: "${key}")`);
  },
};

// --- Environment singleton ---

let _env: NunjucksEnvironment | null = null;

function env(): NunjucksEnvironment {
  if (_env) return _env;

  if (!window.nunjucks) {
    throw new Error('window.nunjucks non disponibile: caricare nunjucks-slim.min.js prima dell\'app');
  }

  _env = new window.nunjucks.Environment(loader);

  _env.addFilter('date', (v: unknown) => {
    if (!v || typeof v !== 'string') return '';
    return formatDate(v);
  });

  _env.addFilter('periodo', (v: unknown) => {
    if (!v || typeof v !== 'string') return '';
    let anno: string, mese: string;
    if (v.includes('_')) { anno = v.split('_')[0]!; mese = v.split('_')[1]!; }
    else if (v.includes('-')) { const p = v.split('-'); anno = p[0]!; mese = p[1]!; }
    else { anno = v.slice(0, 4); mese = v.slice(4, 6); }
    const idx = parseInt(mese, 10) - 1;
    return `${MESI[idx] ?? mese} ${anno}`;
  });

  _env.addFilter('stato_badge', (v: unknown) => {
    if (!v || typeof v !== 'string') return 'secondary';
    return STATO_BADGE[v] ?? 'secondary';
  });
  
  _env.addFilter('stato_lavoratore', (v: unknown, extra: unknown = '') => {
    const fallback = `<span class="text-muted mx-1">&mdash;</span>`;
    if (!v || typeof v !== 'string' || v.trim() === '') return new window.nunjucks.runtime.SafeString(fallback);
    const key = v.trim();
    const label = STATO_LAVORATORE[key];
    if (!label) return new window.nunjucks.runtime.SafeString(fallback);
    const badge = STATO_BADGE[key] ?? 'secondary';
    const icon = ICONA_STATO_LAVORATORE[key] ?? 'bi-question-circle';
    const extraStr = typeof extra === 'string' && extra.trim() ? ` ${extra.trim()}` : '';
    const html = `<span class="text-${badge} mx-1" data-tooltip="${label}${extraStr}" data-position="top"><i class="bi ${icon}"></i></span>`;
    return new window.nunjucks.runtime.SafeString(html);
  });
  
  _env.addFilter('icon_sindacato', (v: unknown) => {
    if (v === null || v === undefined || v === '') return '';
    const code = String(v).trim();
    if (!code) return '';
    const info = getSindacatiMap()[code];
    const sigla = info?.siglaCategoriaEdile;
    if (!sigla) return '';
    const html = `<span class="sindacato ${sigla.toLowerCase()} mx-1" data-tooltip="${sigla}" data-position="top"></span>`;
    return new window.nunjucks.runtime.SafeString(html);
  });
  
  _env.addFilter('badge_cassa', (v: unknown) => {
    if (!v || typeof v !== 'string') return '';
    const info = getCasseMap()[v];
    if (!info) {
      return new window.nunjucks.runtime.SafeString(`<span class="badge bg-secondary mx-1">${v}</span>`);
    }
    const html = `<span class="badge bg-${info.badgeClass} mx-1" data-tooltip="${info.nome}" data-position="top">${v}</span>`;
    return new window.nunjucks.runtime.SafeString(html);
  });

  _env.addFilter('json', (v: unknown) => {
    return JSON.stringify(v, null, 2);
  });

  _env.addFilter('stato_delega_badge', (v: unknown) => {
    if (!v || typeof v !== 'string') return 'secondary';
    return STATO_DELEGA_BADGE[v] ?? 'secondary';
  });

  _env.addFilter('nome_sindacato', (v: unknown) => {
    return getSindacatoLabel(v);
  });

  _env.addFilter('stato_label', (v: unknown) => {
    if (!v || typeof v !== 'string') return '';
    return v.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
  });

  _env.addFilter('numero', (v: unknown) => formatNumero(v));

  _env.addFilter('badge_class', (v: unknown, filterName: unknown) => {
    if (!v || typeof v !== 'string') return 'secondary';
    const name = typeof filterName === 'string' ? filterName : 'stato_badge';
    const map = name === 'stato_delega_badge' ? STATO_DELEGA_BADGE : STATO_BADGE;
    return map[v] ?? 'secondary';
  });

  _env.addFilter('badge_text', (v: unknown, filterName: unknown) => {
    const name = typeof filterName === 'string' ? filterName : 'stato_label';
    if (name === 'nome_sindacato') return getSindacatoLabel(v);
    if (!v || typeof v !== 'string') return '';
    // default: stato_label
    return v.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
  });

  return _env;
}

// --- API pubblica ---

export function templateParse(templateId: string, data: Record<string, unknown>): string | null {
  try {
    return env().renderString(loader.getSource(templateId).src, data);
  } catch {
    console.error(`templateParse: errore rendering "${templateId}"`);
    return null;
  }
}

export function templateParseToElement(
  templateId: string,
  target: HTMLElement,
  data: Record<string, unknown>,
): boolean {
  const html = templateParse(templateId, data);
  if (html === null) return false;
  target.innerHTML = html;
  return true;
}

export function templateRenderString(templateString: string, data: Record<string, unknown>): string {
  return env().renderString(templateString, data);
}
